import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Flex, Icon, Text, Heading, Separator } from '@highereducation/hen-ui';
import Step, { MultiQuestionProps } from '@de/components/Step/Step';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import { getConditionalQuestions } from '@de/utils/getConditionalQuestions';
import { getDependencies } from '@de/utils/data-fetch/prefetchDynamicOptions';
import { useMultiQuestionDynamicOptions } from '@de/hooks/useMultiQuestionDynamicOptions';
import { QuestionType, StepType } from '@de/types/enums';
import { RichText } from '@de/components/RichText';
import { resolveQuestionTitle } from '@de/utils/resolveDynamicFilters';
import { flattenStepsForDynamicOptions } from '@de/utils/flattenStepsForDynamicOptions';
import type { Option, Question, Step as FlowStep } from '@de/types/types';

/** Stable comparison for dependency-change detection (avoids JSON.stringify key-order noise). */
const selectionStableKey = (
  selection: Option | Option[] | undefined,
): string => {
  if (selection == null) return '';
  if (Array.isArray(selection)) {
    return [...selection]
      .map((o) => o.optionId ?? String(o.value ?? ''))
      .sort()
      .join(',');
  }
  return String(selection.optionId ?? selection.value ?? '');
};

const isQuestionComplete = (
  question: any,
  getStepMetadataById: (id: string) => any,
): boolean => {
  const stepData = getStepMetadataById(question.id);
  const selectedOptions = stepData.option;

  if (!selectedOptions) return false;

  if (
    question.questionType === QuestionType.MULTIPLE_CHOICE &&
    question.maxSelection &&
    Array.isArray(selectedOptions)
  ) {
    return selectedOptions.length === question.maxSelection;
  }

  return !!selectedOptions;
};

const MultiQuestion: React.FC<MultiQuestionProps> = ({
  stepQuestions = [],
  bulletPoints = [],
  enableDivider = false,
  groupedQuestions,
  automaticNext = false,
  tcpaText,
}) => {
  const {
    currentStepType,
    currentStep,
    getStepMetadataById,
    updateFlowState,
    handleStepForward,
    flowState,
    steps,
    getCachedOptions,
  } = useDynamicExperienceContext();
  const lastQuestionRef = useRef<HTMLDivElement | null>(null);
  const previousQuestionCount = useRef<number>(0);

  // Include MULTI_QUESTION child steps in the list passed to dynamic-options helpers
  // so dependency keys and fetches resolve sibling customFields (top-level `steps`
  // often omits nested `stepQuestions`).
  const stepsForDynamicOptions = useMemo(() => {
    const base = flattenStepsForDynamicOptions(steps ?? []);
    if (
      !currentStep?.id ||
      currentStepType !== StepType.MULTI_QUESTION ||
      stepQuestions.length === 0
    ) {
      return base;
    }

    const next = [...base];
    const parentIndex = next.findIndex((s) => s.id === currentStep.id);
    const mergedParent = {
      ...(parentIndex >= 0 ? next[parentIndex] : currentStep),
      stepQuestions,
    } as FlowStep;

    if (parentIndex >= 0) {
      next[parentIndex] = mergedParent;
    } else {
      next.push(mergedParent);
    }

    for (const q of stepQuestions) {
      if (q.id && !next.some((s) => s.id === q.id)) {
        next.push(q as FlowStep);
      }
    }

    return next;
  }, [steps, currentStep, currentStepType, stepQuestions]);

  const { optionsMap, loadingMap, errorsByQuestionId } =
    useMultiQuestionDynamicOptions(
      stepQuestions,
      flowState,
      stepsForDynamicOptions,
      getCachedOptions as (step: Question) => Option[] | undefined,
    );

  const questionDependencies = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const question of stepQuestions) {
      if (question.dynamicOptions) {
        const deps = getDependencies(question);
        if (deps.length > 0) {
          map[question.id] = deps;
        }
      }
    }
    return map;
  }, [stepQuestions]);

  const previousSelectionsRef = useRef<
    Record<string, Option | Option[] | undefined>
  >({});

  const dependencyClearInitializedRef = useRef(false);
  const dependencyClearMqStepIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const mqId = currentStep?.id;
    if (mqId !== dependencyClearMqStepIdRef.current) {
      dependencyClearMqStepIdRef.current = mqId;
      dependencyClearInitializedRef.current = false;
      previousSelectionsRef.current = {};
    }

    const currentSelections: Record<string, Option | Option[] | undefined> = {};
    const changedCustomFields: string[] = [];

    for (const question of stepQuestions) {
      const currentSelection = getStepMetadataById(question.id)?.option;
      currentSelections[question.id] = currentSelection;

      const prevSelection = previousSelectionsRef.current[question.id];

      if (
        selectionStableKey(prevSelection) !==
        selectionStableKey(currentSelection)
      ) {
        if (question.customField) {
          changedCustomFields.push(question.customField);
        }
      }
    }

    if (!dependencyClearInitializedRef.current) {
      previousSelectionsRef.current = currentSelections;
      dependencyClearInitializedRef.current = true;
      return;
    }

    previousSelectionsRef.current = currentSelections;

    if (changedCustomFields.length === 0) return;

    for (const [dependentQuestionId, dependencies] of Object.entries(
      questionDependencies,
    )) {
      const hasDependencyChanged = dependencies.some((dep) =>
        changedCustomFields.includes(dep),
      );

      if (hasDependencyChanged && currentSelections[dependentQuestionId]) {
        updateFlowState(dependentQuestionId, {});
      }
    }
  }, [
    currentStep?.id,
    stepQuestions,
    getStepMetadataById,
    updateFlowState,
    questionDependencies,
  ]);

  const renderQuestionComponent = useCallback(
    (question: Question) => {
      const opts = optionsMap[question.id];
      const isQuestionLoading = loadingMap[question.id] ?? false;
      const fetchErr = errorsByQuestionId[question.id];

      return (
        <Step
          {...question}
          {...(opts !== undefined && { options: opts })}
          isMultiQuestionChild
          isOptionsLoading={isQuestionLoading && !!question.dynamicOptions}
          dynamicOptionsError={
            fetchErr && question.dynamicOptions ? fetchErr.message : undefined
          }
        />
      );
    },
    [optionsMap, loadingMap, errorsByQuestionId],
  );

  const questionsToShow = getConditionalQuestions(
    stepQuestions,
    getStepMetadataById,
  );

  const lastVisibleId = questionsToShow.length
    ? questionsToShow[questionsToShow.length - 1].id
    : undefined;

  useEffect(() => {
    if (
      currentStep &&
      currentStep.id &&
      currentStepType === StepType.MULTI_QUESTION
    ) {
      const allChildStepResponse = questionsToShow.reduce(
        (acc: any[], question) => {
          const stepData = getStepMetadataById(question.id);
          if (stepData.option) {
            if (Array.isArray(stepData.option)) acc.push(...stepData.option);
            else acc.push(stepData.option);
          }
          return acc;
        },
        [],
      );

      const currentData = getStepMetadataById(currentStep.id);
      const newData =
        allChildStepResponse.length > 0 ? { option: allChildStepResponse } : {};
      const hasChanged =
        JSON.stringify(currentData) !== JSON.stringify(newData);
      if (hasChanged) updateFlowState(currentStep.id, newData);

      const allQuestionsComplete = questionsToShow.every((question) =>
        isQuestionComplete(question, getStepMetadataById),
      );

      if (
        allQuestionsComplete &&
        automaticNext &&
        hasChanged &&
        allChildStepResponse.length > 0
      ) {
        handleStepForward();
      }
    }
  }, [
    questionsToShow,
    currentStep,
    currentStepType,
    updateFlowState,
    getStepMetadataById,
  ]);

  useEffect(() => {
    const currentCount = questionsToShow.length;

    if (currentCount > previousQuestionCount.current && currentCount > 0) {
      lastQuestionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    previousQuestionCount.current = currentCount;
  }, [questionsToShow]);

  return (
    <Flex direction='column'>
      {bulletPoints.length > 0 && (
        <Flex direction='column' align='center' justify='center'>
          {enableDivider && <Separator />}
          <Flex
            direction='column'
            direction-md='row'
            align='center'
            wrap='wrap'
            justify='center'
            gap='md'
          >
            {bulletPoints?.map((step, index) => (
              <Flex key={index} direction='row' align='center' gap='xs'>
                <Icon
                  icon='check-circle'
                  size='sm'
                  color='secondary'
                  aria-hidden
                />
                <Text variant='b3' color='subtext'>
                  {step}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
      <Flex direction='column' gap='sm'>
        <Flex direction='column' gap='sm'>
          {groupedQuestions?.map((group, groupIndex) => {
            const filteredGroupQuestions = group.questions.filter((question) =>
              questionsToShow.some((q) => q.id === question.id),
            );

            if (filteredGroupQuestions.length === 0) return null;

            const isRow = filteredGroupQuestions.length > 1;

            return (
              <Flex
                key={groupIndex}
                direction={isRow ? 'row' : 'column'}
                gap='sm'
              >
                {filteredGroupQuestions.map((childQuestion) => {
                  const isLast = childQuestion.id === lastVisibleId;

                  return (
                    <Flex
                      key={childQuestion.id as string}
                      ref={
                        isLast
                          ? (el) => {
                              lastQuestionRef.current =
                                el as HTMLDivElement | null;
                            }
                          : undefined
                      }
                      direction='column'
                      justify='center'
                      align='stretch'
                      data-question-id={childQuestion.id as string}
                    >
                      {childQuestion.subtitle?.trim() && (
                        <Heading variant='h3'>
                          {resolveQuestionTitle(
                            childQuestion.subtitle,
                            flowState,
                          )}
                        </Heading>
                      )}
                      {renderQuestionComponent(childQuestion)}
                    </Flex>
                  );
                })}
              </Flex>
            );
          })}
        </Flex>
        {tcpaText && (
          <Flex>
            <RichText
              html={tcpaText}
              variant='l1'
              color='subtext'
              className='!leading-[1.3] [&_p]:!leading-[1.3] [&_li]:!leading-[1.3] [&_ul]:!mb-0'
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default MultiQuestion;
